import React, { useState, useRef } from "react";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import AICoachFloating from "@/components/lingo/AICoachFloating";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Mic, StopCircle, Sparkles, Play } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_PROMPT = "Describe a hobby you have enjoyed for a long time. You should say what it is, how often you do it, why you enjoy it, and how it has changed over time.";

export default function SpeakingExaminer() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(SAMPLE_PROMPT);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState(null);
  const [examType, setExamType] = useState(user?.test_type || "IELTS_ACADEMIC");
  const mediaRef = useRef(null);
  const chunks = useRef([]);
  const startTs = useRef(0);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const m = new MediaRecorder(stream);
      mediaRef.current = m;
      chunks.current = [];
      m.ondataavailable = (e) => chunks.current.push(e.data);
      m.onstop = async () => {
        clearInterval(timerRef.current);
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        setAudioUrl(URL.createObjectURL(blob));
        setTranscribing(true);
        try {
          const file = new File([blob], "speech.webm", { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", file);
          const r = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setTranscript(r.data.text);
          toast.success("Transcript ready");
        } catch (e) { toast.error("Transcription failed"); }
        finally { setTranscribing(false); }
      };
      m.start();
      setRecording(true);
      startTs.current = Date.now();
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(Math.round((Date.now() - startTs.current) / 1000)), 250);
    } catch (e) { toast.error("Mic permission denied"); }
  };
  const stop = () => { mediaRef.current?.stop(); setRecording(false); };

  const evaluate = async () => {
    if (!transcript) return toast.error("Record something first");
    setEvaluating(true);
    try {
      const r = await api.post("/ai/evaluate-speaking", { exam_type: examType, prompt, transcript, duration_sec: duration });
      setResult(r.data);
    } catch (e) { toast.error("Eval failed"); }
    finally { setEvaluating(false); }
  };

  return (
    <DashboardLayout title="AI Speaking Examiner">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recorder card */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <select value={examType} onChange={(e) => setExamType(e.target.value)} data-testid="se-exam" className="bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-3 py-2 text-sm">
              <option value="IELTS_ACADEMIC">IELTS Academic</option>
              <option value="IELTS_GENERAL">IELTS General</option>
              <option value="PTE">PTE</option>
              <option value="OXFORD_ELLT">Oxford ELLT</option>
              <option value="DUOLINGO">Duolingo</option>
            </select>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Prompt</div>
          <textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="se-prompt"
            className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#F77F00] text-sm" />

          <div className="mt-6 relative rounded-3xl border border-slate-200 p-8 text-center">
            {recording && <div className="absolute inset-0 rounded-3xl ai-glow pointer-events-none" />}
            <div className={`mx-auto w-24 h-24 rounded-full ${recording ? "bg-red-500/20 border border-red-500/50" : "bg-[#FFF4E5] border border-[#F77F00]"} flex items-center justify-center mb-4`}>
              <Mic className={`w-10 h-10 ${recording ? "text-red-400" : "text-[#F77F00]"}`} />
            </div>
            <div className="font-mono text-3xl">{Math.floor(duration / 60).toString().padStart(2, "0")}:{(duration % 60).toString().padStart(2, "0")}</div>
            {!recording ? (
              <button onClick={start} data-testid="se-record-btn" className="mt-5 px-6 py-3 rounded-full bg-[#F77F00] text-white font-medium hover:bg-slate-100/90 inline-flex items-center gap-2">
                <Mic className="w-4 h-4" /> Start recording
              </button>
            ) : (
              <button onClick={stop} data-testid="se-stop-btn" className="mt-5 px-6 py-3 rounded-full bg-red-600 hover:bg-red-500 inline-flex items-center gap-2">
                <StopCircle className="w-4 h-4" /> Stop
              </button>
            )}
            {audioUrl && (
              <audio controls src={audioUrl} className="mt-5 mx-auto w-full" data-testid="se-audio" />
            )}
          </div>

          {transcript && (
            <div className="mt-5">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Transcript</div>
              <div className="text-sm text-slate-700 p-3 rounded-xl border border-slate-200 leading-relaxed" data-testid="se-transcript">{transcript}</div>
            </div>
          )}

          <button onClick={evaluate} disabled={!transcript || evaluating || transcribing} data-testid="se-evaluate-btn"
            className="mt-5 w-full bg-[#F77F00] text-white font-medium rounded-xl py-3 hover:bg-slate-100/90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {transcribing ? "Transcribing..." : evaluating ? "Evaluating..." : (<><Sparkles className="w-4 h-4" /> Evaluate speaking</>)}
          </button>
        </div>

        {/* Result */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          {!result ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-16">
              <Sparkles className="w-10 h-10 mb-4 text-[#0047FF]" />
              <div className="font-display text-2xl text-slate-900">Predicted band will appear here</div>
              <div className="text-sm mt-2 max-w-xs">Across fluency, pronunciation, lexical resource and grammar — with hotspots.</div>
            </div>
          ) : (
            <div data-testid="se-result">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Predicted Band</div>
              <div className="font-display text-7xl mt-2">{result.overall_band}</div>
              <div className="mt-6 space-y-3">
                {Object.entries(result.criteria || {}).map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex justify-between"><div className="text-sm capitalize">{k.replaceAll("_", " ")}</div><div className="font-mono">{v.score}</div></div>
                    <div className="text-xs text-slate-500 mt-1.5">{v.comment}</div>
                  </div>
                ))}
              </div>
              {result.pronunciation_hotspots?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Pronunciation hotspots</div>
                  <div className="flex flex-wrap gap-2">
                    {result.pronunciation_hotspots.map((p, i) => (
                      <span key={i} className="px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-xs text-amber-300">{p}</span>
                    ))}
                  </div>
                </div>
              )}
              <p className="mt-6 text-sm text-slate-700 leading-relaxed">{result.feedback}</p>
            </div>
          )}
        </div>
      </div>
      <AICoachFloating />
    </DashboardLayout>
  );
}
